// src/App.tsx
import "../Report.css";

import ReportHeader from "@/components/ReportHeader";
import ReportFooter from "@/components/ReportFooter";
import { CalendarDays } from "lucide-react";
import PieChartComponent from "../common/PieChartComponent";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { AlcanceComparison } from "@/types";
import { getApiUrl } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { categoriesAtom } from "@/store/categories";
import { levelsAtom } from "@/store/levels";
import { useTranslation } from "react-i18next";

interface IntroPageProps {
  title: string;
  page?: number;
}

export const ComparativaPage = ({ title, page }: IntroPageProps) => {
  const { token } = useAuth();
  const { t } = useTranslation();

  const [company] = useAtom(companyAtom);
  const [dates] = useAtom(datesAtom);
  const [categories] = useAtom(categoriesAtom);
  const [levels] = useAtom(levelsAtom);

  const { data } = useQuery<AlcanceComparison>({
    queryKey: [
      "alcances-comparison",
      company?.id,
      new Date(dates?.fromDate || "").getFullYear(),
    ],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `companies/${company?.id}/alcances-comparison/?categories=${categories?.join(",")}&year=${new Date(dates?.fromDate || "").getFullYear()}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      return response.json();
    },
    enabled: !!token && !!company && !!dates,
  });

  return (
    <section className="pdf-page">
      <ReportHeader title={title} />
      <div className="pdf-content">
        <div className="px-2 grid grid-cols-2 gap-x-4 gap-y-0 py-16">
          <PieChartComponent
            data={[
              {
                name: t("Alcance 1"),
                value: Number(data?.current?.alcance_1),
                level: 1,
              },
              {
                name: t("Alcance 2"),
                value: Number(data?.current?.alcance_2),
                level: 2,
              },
            ].filter((item: any) => levels?.includes(Number(item.level)))}
            title={String(new Date(dates?.fromDate || "").getFullYear())}
            icon={<CalendarDays className="h-8 w-8" />}
          />
          <PieChartComponent
            data={[
              {
                name: t("Alcance 1"),
                value: Number(data?.previous?.alcance_1),
                level: 1,
              },
              {
                name: t("Alcance 2"),
                value: Number(data?.previous?.alcance_2),
                level: 2,
              },
            ].filter((item: any) => levels?.includes(Number(item.level)))}
            title={String(new Date(dates?.fromDate || "").getFullYear() - 1)}
            icon={<CalendarDays className="h-8 w-8" />}
          />
        </div>
      </div>
      <ReportFooter page={page} />
    </section>
  );
};
